import React, { useState, useEffect } from 'react';
import { listOwnGroups, listItemsInGroup, addItemToGroup, removeItemFromGroup, deleteGroup } from '../composables/fetchAPI';
import { isCryptoSymbol, cryptoPairLabel } from '../composables/fetchCrypto';
import SearchBar from './SearchBar';
import AddEditGroupPopup from './AddEditGroupPopup';
import FavoriteSwipe from './FavoriteSwipe';
import toast from 'react-hot-toast';
import { useMarketPrices } from '../context/MarketPriceContext'; // ปรับ Path ตามโครงสร้างจริงของคุณ

// PR #72: แสดง displaySymbol (BTC) แต่ดึงราคา/เปิดกราฟด้วย providerSymbol (X:BTCUSD)
const favLabel = (item: any): string => cryptoPairLabel(item?.providerSymbol || item?.symbol) || item?.displaySymbol || '';
const favQuoteSymbol = (item: any): string => item?.providerSymbol || item?.symbol;

export default function Favorite({ onSelect }: { onSelect: (symbol: string) => void }) {
  const [groups, setGroups] = useState<any[]>([]); // Initialize as an array
  const [selectedGroup, setSelectedGroup] = useState('');
  const [selectedGroupName, setSelectedGroupName] = useState(''); // Store the group name
  const [selectedGroupData, setSelectedGroupData] = useState<any>(null); // Store the selected group data for editing
  const [items, setItems] = useState<any[]>([]); // Ensure items is initialized as an array
  const [showDropdown, setShowDropdown] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [popupMode, setPopupMode] = useState<'add' | 'edit'>('add'); // Track the popup mode
  const [refreshTrigger, setRefreshTrigger] = useState(false); // Trigger for refetching items
  const [activeSymbol, setActiveSymbol] = useState<string | null>(null); // ตัวที่เพิ่งเลือกจากช่องค้นหา
  const { currentPrices, fetchPricesForSymbols } = useMarketPrices();

  // Fetch groups when the component mounts
  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const response = await listOwnGroups();
        if (response?.groups && Array.isArray(response.groups)) {
          setGroups(response.groups); // Set the groups array from the response
          const defaultGroupId = response.defaultGroupId || '';
          setSelectedGroup(defaultGroupId); // Set the default group ID
          const defaultGroup = response.groups.find((group: any) => group.id === defaultGroupId);
          setSelectedGroupName(defaultGroup?.name || ''); // Set the default group name
        } else {
          console.error('Unexpected response format:', response);
          setGroups([]);
        }
      } catch (error) {
        console.error('Failed to fetch groups:', error);
        setGroups([]); // Fallback to an empty array in case of error
      }
    };
    fetchGroups();
  }, []);

  // Fetch items when a group is selected
  // Fetch items when a group is selected
  useEffect(() => {
    const fetchGroupItems = async () => {
      if (!selectedGroup) return;
      try {
        const itemList = await listItemsInGroup(selectedGroup);
        if (Array.isArray(itemList.items)) {
          setItems(itemList.items);

          // 🌟 3. สั่งให้ Context ดึงราคาของหุ้นทุกตัวในกลุ่มนี้มารอไว้
          const symbols = itemList.items.map(favQuoteSymbol);
          if (symbols.length > 0) {
            fetchPricesForSymbols(symbols);
          }
        } else {
          setItems([]);
        }
      } catch (error) {
        console.error('Failed to fetch items in group:', error);
        setItems([]);
      }
    };

    fetchGroupItems();
  }, [selectedGroup, refreshTrigger]);
  // 🌟 4. สร้าง Object ราคาสำหรับส่งให้ FavoriteSwipe โดยดึงตรงจาก currentPrices
  const favoritePrices = items.reduce((acc: { [symbol: string]: number }, item: any) => {
    acc[favQuoteSymbol(item)] = currentPrices[favQuoteSymbol(item)] || 0;
    return acc;
  }, {});
  const handleDeleteGroup = async (groupId: string) => {
    try {
      await deleteGroup(groupId);
      toast.success('ลบกลุ่มเรียบร้อยแล้ว');
      handleGroupUpdated(); // รีเฟรชรายการกลุ่ม
    } catch (error: any) {
      console.error('Failed to delete group:', error);
      toast.error('ลบไม่สำเร็จ กรุณาตั้ง Default กับ Group อื่นก่อนลบกลุ่มนี้');
    }
  };
  // Refresh group list after creating or editing a group
  const handleGroupUpdated = async () => {
    try {
      const response = await listOwnGroups();
      if (response?.groups && Array.isArray(response.groups)) {
        setGroups(response.groups);
        const defaultGroupId = response.defaultGroupId || '';
        setSelectedGroup(defaultGroupId); // Reset to default group ID
        const defaultGroup = response.groups.find((group: any) => group.id === defaultGroupId);
        setSelectedGroupName(defaultGroup?.name || ''); // Reset to default group name
      } else {
        console.error('Unexpected response format:', response);
        setGroups([]);
      }
    } catch (error) {
      console.error('Failed to refresh groups:', error);
    }
  };

  // Handle group selection
  const handleGroupSelection = (groupId: string) => {
    setSelectedGroup(groupId);
    const selectedGroup = groups.find((group: any) => group.id === groupId);
    setSelectedGroupName(selectedGroup?.name || '');
    setShowDropdown(false);
  };

  // Handle item removal
  const handleRemoveItem = async (itemId: string) => {
    try {
      // ค้นหาชื่อหุ้นจาก items โดยใช้ itemId
      const itemToRemove = items.find((item) => item.id === itemId);
      const itemName = favLabel(itemToRemove) || 'หุ้นที่ไม่ทราบชื่อ'; // ใช้ชื่อหุ้น หรือข้อความ fallback

      await removeItemFromGroup(itemId);
      toast.success(`ลบหุ้น ${itemName} สำเร็จ`); // แสดงชื่อหุ้นใน toast
      setItems((prevItems) => prevItems.filter((item) => item.id !== itemId)); // Remove item from state
    } catch (error) {
      console.error('Failed to remove item from group:', error);
      toast.error(`ลบหุ้น ${itemId} ไม่สำเร็จ`);
    }
  };
  // Open popup in "add" mode
  const openAddPopup = () => {
    setPopupMode('add');
    setSelectedGroupData(null); // Clear group data for adding
    setShowPopup(true);
  };

  // Open popup in "edit" mode
  const openEditPopup = () => {
    setPopupMode('edit');
    const groupData = groups.find((group: any) => group.id === selectedGroup);
    setSelectedGroupData(groupData); // Pass the selected group data for editing
    setShowPopup(true);
  };

  // เพิ่มเข้ากลุ่มจากผล SearchBar: หุ้นได้ ticker (AAPL), คริปโตได้ providerSymbol (X:BTCUSD)
  // PR #72: คริปโตต้องส่ง assetType: 'CRYPTO' ทุกครั้ง ไม่งั้นถูกบันทึกเป็นหุ้น / หุ้นส่ง { symbol } แบบเดิม
  const handleAddItemToGroup = async (symbol: string) => {
    const isCrypto = isCryptoSymbol(symbol);
    const label = cryptoPairLabel(symbol);
    // เลือกจากช่องค้นหา = เปิดกระดานเทรดตัวนั้นด้วย และดันขึ้นลำดับแรก (ใช้ key เดียวกับการกดใน FavoriteSwipe)
    localStorage.setItem('selectedSymbol', symbol);
    localStorage.setItem(`last_click_${symbol}`, Date.now().toString());
    setActiveSymbol(symbol);
    onSelect(symbol);
    try {
      if (!selectedGroup) {
        console.error('No group selected');
        toast.error('กรุณาเลือกหรือสร้างกลุ่มก่อนเพิ่มหุ้น');
        return;
      }
      // มีอยู่แล้ว: แค่เปิดกระดาน + ดันขึ้นบนสุด (ทำไปแล้วด้านบน) ไม่ต้องเพิ่มซ้ำ
      if (items.some((item) => favQuoteSymbol(item) === symbol)) return;
      await addItemToGroup(
        isCrypto
          ? { groupId: selectedGroup, assetType: 'CRYPTO', symbol, note: '', alias: '' }
          : { groupId: selectedGroup, symbol, note: '', alias: '' }
      );
      toast.success(`เพิ่ม ${label} ในรายการโปรดแล้ว`);
      setRefreshTrigger((prev) => !prev); // Toggle refreshTrigger to refetch items
    } catch (error: any) {
      console.error('Failed to add item to group:', error);
      const msg = String(error?.message || '');
      if (/already exists/i.test(msg)) toast.error(`${label} อยู่ในกลุ่มนี้แล้ว`);
      else if (isCrypto) toast.error(`ไม่สามารถเพิ่ม ${label} ได้: ${error?.th_message || msg || 'ไม่พบเหรียญในรายการที่รองรับ'}`);
      else toast.error('ไม่สามารถเพิ่มหุ้นในกลุ่มได้');
    }
  };

  return (
    <div className="p-3 md:p-4 rounded-xl shadow-lg h-full" style={{ backgroundColor: 'var(--component-bg-color)' }}>

      {/* Header Section */}
      <div className="flex justify-between items-center mb-2 md:mb-4 px-1">
        <div className="text-lg md:text-xl font-bold" style={{ color: 'var(--text-color)' }}>
          รายการโปรด
        </div>
        <button
          onClick={openEditPopup}
          className="p-2 hover:bg-white/10 rounded-full transition-colors"
          style={{ color: 'var(--text-color)' }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5 md:w-6 md:h-6">
            <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 3.487a2.25 2.25 0 113.182 3.182L8.25 18.464l-4.5 1.5 1.5-4.5L16.862 3.487z" />
          </svg>
        </button>
      </div>

      {/* Group List Section - ปรับให้ Compact ขึ้นบน Mobile */}
      <div className="mb-4">
        <div
          className="flex space-x-3 md:space-x-4 overflow-x-auto py-1 items-start"
          style={{
            whiteSpace: 'nowrap',
            scrollbarWidth: 'thin',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {groups.map((group: any) => (
            <div key={group.id} className="flex flex-col items-center flex-shrink-0 group relative">
              <button
                onClick={() => handleGroupSelection(group.id)}
                className="px-4 py-2 text-sm md:text-base font-bold rounded-xl md:rounded-lg transition-all shadow-sm active:scale-95"
                style={{
                  color: selectedGroup === group.id ? 'black' : 'var(--text-color)',
                  backgroundColor: selectedGroup === group.id ? '#fde047' : 'var(--hover-item)',
                }}
              >
                {group.name}
              </button>

              {group.isDefault && (
                <span className="text-[10px] md:text-xs mt-1.5 font-bold opacity-60 uppercase tracking-tighter" style={{ color: 'var(--secondary-text-color)' }}>
                  Default
                </span>
              )}

              {/* Delete Button - ปรับให้กดง่ายขึ้นบน Mobile */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (window.confirm(`คุณแน่ใจหรือไม่ว่าต้องการลบกลุ่ม "${group.name}"?`)) {
                    handleDeleteGroup(group.id);
                  }
                }}
                className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-[10px] shadow-md md:opacity-0 md:group-hover:opacity-100 transition-opacity"
              >
                ✕
              </button>
            </div>
          ))}

          {/* Add Group Button */}
          <div className="flex flex-col items-center">
            <button
              onClick={openAddPopup}
              className="px-4 py-2 font-bold bg-blue-500 text-white rounded-xl md:rounded-lg shadow-md active:scale-95"
            >
              +
            </button>
            <span className="text-[10px] mt-1.5 invisible">จองที่</span>
          </div>
        </div>
      </div>

      {/* ค้นหา 2 โหมด (หุ้น / Crypto) ใช้ SearchBar ตัวเดียวกับ header — เลือกแล้วเพิ่มเข้ากลุ่มทันที */}
      <div className="relative px-1">
        <SearchBar
          onSelect={handleAddItemToGroup}
          selectedSymbols={items.map(favQuoteSymbol)}
          persistSelection={false}
          includeCrypto
        />
      </div>

      {/* Favorite Items List (FavoriteSwipe) */}
      <div className="mt-2 flex-grow overflow-hidden">
        <FavoriteSwipe items={items} prices={favoritePrices} onRemove={handleRemoveItem} onSelect={onSelect} activeSymbol={activeSymbol} />
      </div>

      {/* AddEditGroupPopup */}
      {showPopup && (
        <AddEditGroupPopup
          onClose={() => setShowPopup(false)}
          onGroupUpdated={handleGroupUpdated}
          mode={popupMode}
          groupData={selectedGroupData}
        />
      )}
    </div>
  );
}